import type { SiteTexts } from './index';
import { INFORM } from './texts-inform';
import { STATS_RUNNING } from './texts-stats';
import { LANG_ALIASES, WORLD_TEXTS } from './texts-world';

/** Built-in banner translations. Site text in a language overrides these. */

export const EN_TEXTS: SiteTexts = {
  title: 'We use cookies',
  description: 'We use cookies to run this site. With your permission we also measure visits and show relevant content.',
  acceptAll: 'Accept all',
  rejectAll: 'Reject all',
  settings: 'Settings',
  save: 'Save choices',
  manage: 'Cookie settings',
  privacyChoices: 'Your Privacy Choices',
  gpcHonored: 'Opt-out request honored',
  privacyPolicy: 'Privacy policy',
  cookieDeclaration: 'Cookie declaration',
  always: 'Always on',
  blockedEmbed: 'This content is blocked until you accept marketing cookies.',
  loadEmbed: 'Load content',
  notice: 'We use cookies to run this site and count visits anonymously.',
  object: 'Object',
  doNotSell: 'Do not sell or share my personal information',
  poweredBy: 'Powered by',
  categories: {
    necessary: { name: 'Necessary', description: 'Required for the site to work. Cannot be switched off.' },
    functionality: { name: 'Preferences', description: 'Remember choices such as language or region.' },
    analytics: { name: 'Analytics', description: 'Help us understand how the site is used.' },
    marketing: { name: 'Marketing', description: 'Used to show relevant ads and measure campaigns.' },
  },
};


const DE: SiteTexts = {
  title: 'Wir verwenden Cookies',
  description: 'Wir verwenden Cookies, um diese Website zu betreiben. Mit Ihrer Erlaubnis messen wir auch Zugriffe und zeigen relevante Inhalte.',
  acceptAll: 'Alle akzeptieren',
  rejectAll: 'Alle ablehnen',
  settings: 'Einstellungen',
  save: 'Auswahl speichern',
  manage: 'Cookie-Einstellungen',
  privacyChoices: 'Ihre Datenschutzoptionen',
  gpcHonored: 'Opt-out-Anfrage umgesetzt',
  privacyPolicy: 'Datenschutzerklärung',
  cookieDeclaration: 'Cookie-Erklärung',
  always: 'Immer aktiv',
  blockedEmbed: 'Dieser Inhalt wird erst nach Zustimmung zu Marketing-Cookies geladen.',
  loadEmbed: 'Inhalt laden',
  notice: 'Wir verwenden Cookies, um diese Website zu betreiben und Besuche anonym zu zählen.',
  object: 'Widersprechen',
  doNotSell: 'Meine Daten nicht verkaufen oder weitergeben',
  poweredBy: 'Bereitgestellt von',
  categories: {
    necessary: { name: 'Notwendig', description: 'Erforderlich für den Betrieb der Website. Kann nicht deaktiviert werden.' },
    functionality: { name: 'Präferenzen', description: 'Speichert Einstellungen wie Sprache oder Region.' },
    analytics: { name: 'Statistik', description: 'Hilft uns zu verstehen, wie die Website genutzt wird.' },
    marketing: { name: 'Marketing', description: 'Für relevante Werbung und Kampagnenmessung.' },
  },
};

const FR: SiteTexts = {
  title: 'Nous utilisons des cookies',
  description: 'Nous utilisons des cookies pour faire fonctionner ce site. Avec votre accord, nous mesurons aussi l’audience et affichons des contenus pertinents.',
  acceptAll: 'Tout accepter',
  rejectAll: 'Tout refuser',
  settings: 'Paramètres',
  save: 'Enregistrer',
  manage: 'Paramètres des cookies',
  privacyChoices: 'Vos choix de confidentialité',
  gpcHonored: 'Demande d’opposition prise en compte',
  privacyPolicy: 'Politique de confidentialité',
  cookieDeclaration: 'Déclaration relative aux cookies',
  always: 'Toujours actif',
  blockedEmbed: 'Ce contenu est bloqué tant que vous n’acceptez pas les cookies marketing.',
  loadEmbed: 'Charger le contenu',
  notice: 'Nous utilisons des cookies pour faire fonctionner ce site et compter les visites de façon anonyme.',
  object: 'S’opposer',
  doNotSell: 'Ne pas vendre ni partager mes informations personnelles',
  poweredBy: 'Propulsé par',
  categories: {
    necessary: { name: 'Nécessaires', description: 'Indispensables au fonctionnement du site. Ne peuvent pas être désactivés.' },
    functionality: { name: 'Préférences', description: 'Mémorisent vos choix comme la langue ou la région.' },
    analytics: { name: 'Statistiques', description: 'Nous aident à comprendre l’utilisation du site.' },
    marketing: { name: 'Marketing', description: 'Servent à afficher des publicités pertinentes et mesurer les campagnes.' },
  },
};

const ES: SiteTexts = {
  title: 'Usamos cookies',
  description:
    'Usamos cookies para que este sitio funcione y, con tu consentimiento, para medir el tráfico y personalizar el contenido. Puedes cambiar tu elección en cualquier momento.',
  acceptAll: 'Aceptar todo',
  rejectAll: 'Rechazar todo',
  settings: 'Configuración',
  save: 'Guardar selección',
  manage: 'Configuración de cookies',
  privacyChoices: 'Tus opciones de privacidad',
  gpcHonored: 'Solicitud de exclusión atendida',
  privacyPolicy: 'Política de privacidad',
  cookieDeclaration: 'Declaración de cookies',
  always: 'Siempre activo',
  blockedEmbed: 'Este contenido está bloqueado hasta que aceptes las cookies de marketing.',
  loadEmbed: 'Cargar contenido',
  notice: 'Usamos cookies para que este sitio funcione y contar las visitas de forma anónima.',
  object: 'Oponerse',
  doNotSell: 'No vender ni compartir mi información personal',
  poweredBy: 'Con tecnología de',
  categories: {
    necessary: { name: 'Necesarias', description: 'Imprescindibles para que el sitio funcione. No se pueden desactivar.' },
    functionality: { name: 'Preferencias', description: 'Recuerdan opciones como el idioma o la región.' },
    analytics: { name: 'Estadísticas', description: 'Nos ayudan a entender cómo se usa el sitio.' },
    marketing: { name: 'Marketing', description: 'Para mostrar anuncios relevantes y medir campañas.' },
  },
};

const IT: SiteTexts = {
  title: 'Utilizziamo i cookie',
  description:
    'Utilizziamo i cookie per far funzionare questo sito e, con il tuo consenso, per misurare il traffico e personalizzare i contenuti. Puoi cambiare la tua scelta in qualsiasi momento.',
  acceptAll: 'Accetta tutto',
  rejectAll: 'Rifiuta tutto',
  settings: 'Impostazioni',
  save: 'Salva le scelte',
  manage: 'Impostazioni cookie',
  privacyChoices: 'Le tue scelte sulla privacy',
  gpcHonored: 'Richiesta di opposizione rispettata',
  privacyPolicy: 'Informativa sulla privacy',
  cookieDeclaration: 'Dichiarazione sui cookie',
  always: 'Sempre attivo',
  blockedEmbed: 'Questo contenuto è bloccato finché non accetti i cookie di marketing.',
  loadEmbed: 'Carica contenuto',
  notice: 'Usiamo i cookie per far funzionare questo sito e contare le visite in forma anonima.',
  object: 'Mi oppongo',
  doNotSell: 'Non vendere né condividere i miei dati personali',
  poweredBy: 'Realizzato con',
  categories: {
    necessary: { name: 'Necessari', description: 'Indispensabili per il funzionamento del sito. Non possono essere disattivati.' },
    functionality: { name: 'Preferenze', description: 'Ricordano scelte come lingua o regione.' },
    analytics: { name: 'Statistiche', description: 'Ci aiutano a capire come viene usato il sito.' },
    marketing: { name: 'Marketing', description: 'Per mostrare annunci pertinenti e misurare le campagne.' },
  },
};

const NL: SiteTexts = {
  title: 'Wij gebruiken cookies',
  description:
    'Wij gebruiken cookies om deze site te laten werken en, met uw toestemming, om bezoek te meten en inhoud te personaliseren. U kunt uw keuze op elk moment wijzigen.',
  acceptAll: 'Alles accepteren',
  rejectAll: 'Alles weigeren',
  settings: 'Instellingen',
  save: 'Keuze opslaan',
  manage: 'Cookie-instellingen',
  privacyChoices: 'Uw privacykeuzes',
  gpcHonored: 'Opt-outverzoek gehonoreerd',
  privacyPolicy: 'Privacybeleid',
  cookieDeclaration: 'Cookieverklaring',
  always: 'Altijd aan',
  blockedEmbed: 'Deze inhoud is geblokkeerd totdat u marketingcookies accepteert.',
  loadEmbed: 'Inhoud laden',
  notice: 'We gebruiken cookies om deze site te laten werken en bezoeken anoniem te tellen.',
  object: 'Bezwaar maken',
  doNotSell: 'Mijn persoonsgegevens niet verkopen of delen',
  poweredBy: 'Mogelijk gemaakt door',
  categories: {
    necessary: { name: 'Noodzakelijk', description: 'Nodig om de site te laten werken. Kan niet worden uitgeschakeld.' },
    functionality: { name: 'Voorkeuren', description: 'Onthouden keuzes zoals taal of regio.' },
    analytics: { name: 'Statistieken', description: 'Helpen ons te begrijpen hoe de site wordt gebruikt.' },
    marketing: { name: 'Marketing', description: 'Voor relevante advertenties en het meten van campagnes.' },
  },
};

/** Adds the inform-mode line and button to each language (English where one is missing). */
function withInform(all: Record<string, SiteTexts>): Record<string, SiteTexts> {
  const out: Record<string, SiteTexts> = {};
  for (const [lang, t] of Object.entries(all)) {
    const [inform, turnOff] = INFORM[lang] ?? INFORM.en!;
    out[lang] = { ...t, inform, turnOff, statsRunning: STATS_RUNNING[lang] ?? STATS_RUNNING.en! };
  }
  return out;
}

export const BUILTIN_TEXTS: Record<string, SiteTexts> = /* @__PURE__ */ withInform(/* @__PURE__ */ Object.assign({ en: EN_TEXTS, de: DE, fr: FR, es: ES, it: IT, nl: NL }, WORLD_TEXTS));

/**
 * The banner's words for a visitor, picked from the languages the page and browser offer (in order):
 * the site's own text in that language over the built-in translation, else the built-in
 * translation, else the site's default language, else English. Codes are matched in full ("pt-br")
 * and then by language ("pt").
 */
export function resolveTexts(site: Record<string, SiteTexts>, defaultLang: string, candidates: string[]): { lang: string; texts: SiteTexts } {
  const tried = new Set<string>();
  const order: string[] = [];
  for (const raw of [...candidates, defaultLang, 'en']) {
    const full = (raw || '').toLowerCase().trim();
    if (!/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/.test(full)) continue;
    for (const c of [LANG_ALIASES[full] ?? full, LANG_ALIASES[full.slice(0, 2)] ?? full.slice(0, 2)]) {
      if (!tried.has(c)) {
        tried.add(c);
        order.push(c);
      }
    }
  }
  for (const c of order) {
    const builtin = BUILTIN_TEXTS[c];
    const own = site[c];
    if (own) {
      const base = builtin ?? BUILTIN_TEXTS.en!;
      return { lang: c, texts: { ...base, ...own, categories: { ...base.categories, ...own.categories } } };
    }
    if (builtin) return { lang: c, texts: builtin };
  }
  return { lang: 'en', texts: BUILTIN_TEXTS.en! };
}

